import { SlotCanvasLayout } from "@/components/ai-elements/slot-canvas";
import { ArtifactDownloadAction } from "./ArtifactDownloadAction";
import { ArtifactPageContent, artifactSubtitle } from "./ArtifactPageContent";

export interface ArtifactCanvasBodyProps {
  title: string;
  /** Presigned URL serving the version on screen. */
  contentSrc?: string;
  /** The same bytes, presigned to arrive as a named file. */
  downloadUrl?: string;
  /** Version being shown, which keys the iframe so a reload actually happens. */
  version?: number;
  /** The artifact's current version, to say when an older one is on screen. */
  latestVersion?: number;
  /** True until the artifact's details have ever loaded. */
  isPreparing: boolean;
  /** True when the artifact is gone (deleted, or never belonged to us). */
  isMissing: boolean;
}

/**
 * Body of the artifact canvas: the page itself, and a way to keep it.
 *
 * Both the preview and the download point straight at object storage, so the
 * page is fetched from an origin that holds none of this app's cookies and its
 * scripts have nothing to reach.
 */
export function ArtifactCanvasBody({
  title,
  contentSrc,
  downloadUrl,
  version,
  latestVersion,
  isPreparing,
  isMissing,
}: ArtifactCanvasBodyProps) {
  return (
    <SlotCanvasLayout
      title={title}
      subtitle={artifactSubtitle(version, latestVersion)}
      // Gated on there being a page to keep, not merely a URL: a host that had
      // one before it could render would otherwise offer a download beside the
      // "opening your artifact" splash.
      headerActions={isPreparing || isMissing
        ? undefined
        : <ArtifactDownloadAction downloadUrl={downloadUrl} />}
      bodyClassName="overflow-hidden"
    >
      <ArtifactPageContent
        title={title}
        contentSrc={contentSrc}
        version={version}
        isPreparing={isPreparing}
        isMissing={isMissing}
      />
    </SlotCanvasLayout>
  );
}
